/**
 * The V2 theme store.
 *
 * A zustand store rather than a React context ON PURPOSE (UX spec §6a): the
 * Stage and the live Rail must be context-free so `React.memo` on a zero-prop
 * leaf is airtight. A leaf that needs colour reads `useV2Theme(s => s.tokens)`
 * — one subscription that fires only when the user flips the theme.
 */
import {create} from 'zustand';

import {getPrefSync, setPref} from '@/Store/liveGame/platform/storage';

import {TOKENS, V2ThemeName, V2Tokens} from './tokens';

const STORAGE_KEY = 'play_v2_theme';

/**
 * The CircleChess palette is the DEFAULT (M2 spec §3.3): the classic lobby and
 * the game then match the brand the rest of the app is drawn in, out of the
 * box. Light and Dark stay selectable from the theme switch / the toolbar tile.
 */
export const DEFAULT_THEME: V2ThemeName = 'circlechess';

/**
 * Read through `platform/storage`'s SYNCHRONOUS half (M2 spec §5.1): the theme
 * is needed before the first paint, so an async read would flash the default
 * palette on every load. A platform without a synchronous store gets
 * `DEFAULT_THEME`.
 */
const readStored = (): V2ThemeName => {
  const raw = getPrefSync(STORAGE_KEY);
  return raw && raw in TOKENS ? (raw as V2ThemeName) : DEFAULT_THEME;
};

const writeStored = (name: V2ThemeName): void => {
  setPref(STORAGE_KEY, name);
};

export type V2ThemeStore = {
  name: V2ThemeName;
  tokens: V2Tokens;
  setTheme: (name: V2ThemeName) => void;
};

export const useV2Theme = create<V2ThemeStore>(set => {
  const name = readStored();
  return {
    name,
    tokens: TOKENS[name],
    setTheme: next => {
      writeStored(next);
      set({name: next, tokens: TOKENS[next]});
    },
  };
});

/** For non-React code (the sounds subscription, the lab's fixture builder). */
export const getTokens = (): V2Tokens => useV2Theme.getState().tokens;

export default useV2Theme;
